'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import Icon from '@/components/ui/AppIcon';

interface Partnership {
  id: number;
  name: string;
  logo: string;
  alt: string;
  status: string;
  year: string;
  description: string;
  capabilities: string[];
  certifications: string[];
}

interface PartnershipShowcaseProps {
  className?: string;
}

const PartnershipShowcase = ({ className = '' }: PartnershipShowcaseProps) => {
  const [activePartner, setActivePartner] = useState<number>(1);

  const partnerships: Partnership[] = [
  {
    id: 1,
    name: 'Microsoft',
    logo: "/assets/images/Microsoft-Partner-1767340959771.png",
    alt: 'Microsoft Partner Network logo',
    status: 'Gold Partner',
    year: '2020',
    description: 'As a Microsoft Gold Partner, we deliver comprehensive Azure cloud solutions, Microsoft 365 implementations, and Copilot workshops. Our team holds multiple Microsoft certifications and has successfully migrated 50+ organizations to Microsoft cloud platforms.',
    capabilities: [
    'Azure Cloud Architecture',
    'Microsoft 365 Deployment',
    'Copilot Implementation',
    'Power Platform Solutions',
    'Dynamics 365 Integration',
    'Security & Compliance'],

    certifications: [
    'Azure Solutions Architect Expert',
    'Microsoft 365 Certified',
    'Power Platform Specialist',
    'Security Administrator Associate']

  },
  {
    id: 2,
    name: 'AWS',
    logo: "/assets/images/Screenshot_2026-01-02_at_12.41.30_PM-1767340820772.png",
    alt: 'AWS Partner Network logo',
    status: 'Select Tier Partner',
    year: '2022',
    description: 'Our AWS partnership enables us to deliver scalable data engineering solutions, machine learning implementations, and multi-cloud strategies. We specialize in AWS data services and have architected solutions processing petabytes of data for enterprise clients.',
    capabilities: [
    'Data Lake Architecture',
    'Machine Learning Solutions',
    'Serverless Applications',
    'Database Migration',
    'Analytics & BI',
    'DevOps & Automation'],

    certifications: [
    'AWS Solutions Architect Professional',
    'AWS Data Analytics Specialty',
    'AWS Machine Learning Specialty',
    'AWS DevOps Engineer Professional']

  }];


  const activePartnership = partnerships.find((p) => p.id === activePartner) || partnerships[0];

  return (
    <section className={`py-16 md:py-20 lg:py-24 bg-background ${className}`}>
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-headline font-bold text-primary mb-4">
              Strategic Partnerships
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Certified partnerships with industry leaders enable us to deliver enterprise-grade solutions with proven methodologies and best practices.
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            <div className="lg:w-1/3 space-y-4">
              {partnerships.map((partner) =>
              <button
                key={partner.id}
                onClick={() => setActivePartner(partner.id)}
                className={`w-full p-6 rounded-lg border-2 transition-all duration-base text-left ${
                activePartner === partner.id ?
                'border-primary bg-primary/5 shadow-md' :
                'border-border bg-card hover:border-primary/50 hover:shadow-sm'}`
                }>

                  <div className="flex items-center justify-between mb-4">
                    <div className="relative w-24 h-12">
                      <Image
                      src={partner.logo}
                      alt={partner.alt}
                      fill
                      unoptimized
                      className="object-contain" />


                    </div>
                    <Icon
                    name={activePartner === partner.id ? 'CheckCircleIcon' : 'ChevronRightIcon'}
                    size={24}
                    className={activePartner === partner.id ? 'text-primary' : 'text-muted-foreground'}
                    variant={activePartner === partner.id ? 'solid' : 'outline'} />

                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-headline font-bold text-foreground mb-1">
                        {partner.name}
                      </h3>
                      <p className="text-sm text-muted-foreground">{partner.status}</p>
                    </div>
                    <span className="text-xs font-body text-muted-foreground">Since {partner.year}</span>
                  </div>
                </button>
              )}
            </div>

            <div className="lg:w-2/3">
              <div className="bg-card rounded-lg p-8 shadow-lg border border-border">
                <div className="flex items-center justify-between mb-6">
                  <div className="relative w-32 h-16">
                    <Image
                      src={activePartnership.logo}
                      alt={activePartnership.alt}
                      fill
                      unoptimized
                      className="object-contain" />


                  </div>
                  <div className="text-right">
                    <span className="inline-block px-4 py-1 bg-success/10 text-success text-sm font-body font-semibold rounded-full">
                      {activePartnership.status}
                    </span>
                  </div>
                </div>

                <p className="text-muted-foreground leading-relaxed mb-6">
                  {activePartnership.description}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="text-sm font-headline font-semibold text-foreground mb-3 flex items-center">
                      <Icon name="CogIcon" size={16} className="mr-2 text-primary" />
                      Core Capabilities
                    </h4>
                    <ul className="space-y-2">
                      {activePartnership.capabilities.map((capability, index) =>
                      <li key={index} className="flex items-start">
                          <Icon name="CheckIcon" size={16} className="mr-2 text-success mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-muted-foreground">{capability}</span>
                        </li>
                      )}
                    </ul>
                  </div>

                  <div>
                    <h4 className="text-sm font-headline font-semibold text-foreground mb-3 flex items-center">
                      <Icon name="AcademicCapIcon" size={16} className="mr-2 text-primary" />
                      Team Certifications
                    </h4>
                    <ul className="space-y-2">
                      {activePartnership.certifications.map((cert, index) =>
                      <li key={index} className="flex items-start">
                          <Icon name="ShieldCheckIcon" size={16} className="mr-2 text-accent mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-muted-foreground">{cert}</span>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>

                <div className="pt-6 border-t border-border">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                      <div className="flex items-center">
                        <Icon name="CalendarIcon" size={16} className="mr-1" />
                        <span>Partner since {activePartnership.year}</span>
                      </div>
                      <div className="flex items-center">
                        <Icon name="UserGroupIcon" size={16} className="mr-1" />
                        <span>50+ Projects Delivered</span>
                      </div>
                    </div>
                    <button className="px-6 py-2.5 bg-primary text-primary-foreground font-headline font-semibold text-sm rounded-md hover:bg-secondary shadow-sm hover:shadow-md transition-all duration-base">
                      View Solutions
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default PartnershipShowcase;